import type { FC } from 'react';
import { useCallback } from 'react';
import useIntl from '../../../../../../hooks/useIntl';
import FormattedMessage from '../../../../../../components/FormattedMessage';
import type { ListActivePaymentDto } from '../../../../../../../generated-api';
import { PaymentState } from '../../../../../../../generated-api';
import CartItems from './CartItems';
import { useCartStore } from '../../../../../../context/cart/CartStore';
import { shallow } from 'zustand/shallow';
import { Dialog } from '@ordr-as/umami-react/dialog';
import { useCancelPayment } from '../../../../../../hooks/useCancelPayment';
import { clearPaymentOrigin, getDigitalBillReference } from '../../../../../../utils/paymentOriginTracker';
import { useLocation } from '../../../../../../context/LocationContext';
import { createSelectUnsettledDigitalBillUrl } from '../../../../../../utils/linkBuilder';
import { useNavigate } from 'react-router-dom';

interface Props {
  onCloseDialog: () => void;
  focusedPayment: ListActivePaymentDto;
  isOpen: boolean;
  removeActivePayment: (activePayment: ListActivePaymentDto) => void;
}

const FocusedPaymentDialog: FC<Props> = ({ isOpen, onCloseDialog, focusedPayment, removeActivePayment }) => {
  const intl = useIntl();
  const { location } = useLocation();
  const navigate = useNavigate();
  const { cartIsEmpty, replaceCart } = useCartStore(
    (state) => ({
      cartIsEmpty: state.cart.orderLines.length < 1,
      replaceCart: state.overrideCart,
    }),
    shallow
  );

  const cancelPayment = useCancelPayment();

  const isCancelledPayment = focusedPayment?.paymentState === PaymentState.FAILED;
  const digitalBillReference = getDigitalBillReference(focusedPayment?.paymentReference);

  const closeAndClearOrigin = useCallback(() => {
    if (focusedPayment?.paymentReference) {
      clearPaymentOrigin(focusedPayment.paymentReference);
    }
    onCloseDialog();
  }, [focusedPayment?.paymentReference, onCloseDialog]);

  const discardOrder = useCallback(() => {
    cancelPayment({ focusedPayment, onDone: closeAndClearOrigin, removeActivePayment });
  }, [cancelPayment, closeAndClearOrigin, focusedPayment, removeActivePayment]);

  const setItemsBackInCart = useCallback(() => {
    if (!focusedPayment?.cart) {
      return;
    }
    replaceCart(focusedPayment.cart);
    discardOrder();
  }, [discardOrder, focusedPayment?.cart, replaceCart]);

  const openTable = useCallback(() => {
    cancelPayment({
      focusedPayment,
      onDone: () => {
        closeAndClearOrigin();
        navigate(createSelectUnsettledDigitalBillUrl(location.locationId, digitalBillReference!));
      },
      removeActivePayment,
    });
  }, [
    cancelPayment,
    closeAndClearOrigin,
    digitalBillReference,
    focusedPayment,
    location.locationId,
    navigate,
    removeActivePayment,
  ]);

  if (!isOpen || !focusedPayment) {
    return null;
  }

  // Digital bill payment: show contextual actions instead of cart-specific ones
  if (digitalBillReference) {
    if (isCancelledPayment) {
      return (
        <Dialog
          className="w-full mx-4 tablet:w-1/2 tablet:mx-0"
          onClose={closeAndClearOrigin}
          title={`${focusedPayment.paymentMethod.label} - ${intl.formatMessage({
            id: 'payment.focusDialog.cancelledTitle',
          })}`}
          actionLabel={intl.formatMessage({ id: 'digitalBill.openBillButton' })}
          onAction={openTable}
        >
          <p className="mb-4">
            <FormattedMessage id="payment.digitalbill.focusDialog.cancelledContent" />
          </p>
        </Dialog>
      );
    }

    return (
      <Dialog
        className="w-full mx-4 tablet:w-1/2 tablet:mx-0"
        onClose={closeAndClearOrigin}
        title={`${focusedPayment.paymentMethod.label} - ${intl.formatMessage({
          id: 'payment.focusDialog.inUseTitle',
        })}`}
        actionLabel={intl.formatMessage({ id: 'digitalBill.openBillButton' })}
        onAction={() => {
          onCloseDialog();
          navigate(createSelectUnsettledDigitalBillUrl(location.locationId, digitalBillReference));
        }}
        secondaryActionLabel={intl.formatMessage({ id: 'payment.focusDialog.cancelPayment' })}
        onSecondaryAction={discardOrder}
      >
        <p className="mb-4">
          <FormattedMessage
            id="payment.focusDialog.inUseDigitalBillContent"
            values={{ reference: digitalBillReference }}
          />
        </p>
      </Dialog>
    );
  }

  const hasCartItems = Boolean(focusedPayment.cart?.orderLines?.length);

  return (
    <Dialog
      className="w-full mx-4 tablet:w-1/2 tablet:mx-0"
      onClose={onCloseDialog}
      title={`${focusedPayment.paymentMethod.label} - ${intl.formatMessage({
        id: isCancelledPayment ? 'payment.focusDialog.cancelledTitle' : 'payment.focusDialog.inUseTitle',
      })}`}
      actionLabel={hasCartItems ? intl.formatMessage({ id: 'payment.focusDialog.putBackAction' }) : undefined}
      onAction={hasCartItems ? setItemsBackInCart : undefined}
      isDisabledAction={!cartIsEmpty}
      secondaryActionLabel={intl.formatMessage({ id: 'payment.focusDialog.discardAction' })}
      onSecondaryAction={discardOrder}
    >
      <p className="mb-4">
        {isCancelledPayment ? (
          <FormattedMessage id="payment.focusDialog.cancelledContent" />
        ) : (
          <FormattedMessage id="payment.focusDialog.inUseContent" />
        )}
      </p>
      {hasCartItems && <CartItems orderLines={focusedPayment.cart!.orderLines} />}
    </Dialog>
  );
};

export default FocusedPaymentDialog;
